import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { TextField } from 'core/components/text-field';
import { useToast } from 'core/components/toast';
import chevronLeft from 'core/assets/svg/ic-chevron-left.svg';
import useStyles from './report-issue.styles';

// Sub-view of the offer-flow system error modal. Lets the operator attach an
// optional note and send it to Sentry as user feedback associated with the
// captured error event (GitHub #30224). Offer-flow errors don't create a Sentry
// event on their own, so we capture one here as the parent for the feedback.
const ReportIssue = ({ error, onBack, onClose }) => {
  const classes = useStyles();
  const { notify } = useToast();
  const [details, setDetails] = useState('');
  const [sending, setSending] = useState(false);

  const sendReport = async () => {
    setSending(true);
    try {
      const eventId = Sentry.captureException(
        new globalThis.Error(`Offer-flow system error reported: ${error?.code ?? 'unknown'}`),
        {
          tags: { source: 'offer-system-error-report', errorCode: error?.code },
          extra: { code: error?.code, type: error?.message, timestamp: error?.timestamp }
        }
      );

      Sentry.captureFeedback({
        associatedEventId: eventId,
        message: details?.trim()
          ? details.trim()
          : `Staff reported offer-flow system error (code ${error?.code ?? 'unknown'}).`
      });

      notify({
        type: 'success',
        message: 'Report sent. Thanks for letting us know.',
        durationInSeconds: 3
      });
      onClose();
    } catch {
      // If Sentry is unavailable, don't strand the operator on the sub-modal.
      notify({ type: 'error', message: 'Could not send report. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button type="button" className={classes.backButton} onClick={onBack}>
        <img src={chevronLeft} alt="" className={classes.backIcon} />
        <Label type="body2Bold" className={classes.backLabel}>
          Back to error details
        </Label>
      </button>
      <div className={classes.header}>
        <Label type="title" className={classes.title}>
          Report this issue
        </Label>
        <Label type="body1" className={classes.intro}>
          We&rsquo;ve included the technical details. Add more context if it helps.
        </Label>
      </div>
      <div className={classes.field}>
        <div className={classes.labelRow}>
          <Label type="infoLabel" className={classes.fieldLabel}>
            Additional details
          </Label>
          <Label type="infoLabel" className={classes.optional}>
            Optional
          </Label>
        </div>
        <TextField
          mode="light"
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          placeholder="Add any details that might help"
        />
      </div>
      <div className={classes.buttons}>
        <Button
          onClick={sendReport}
          loading={sending}
          disabled={sending}
          variant="contained"
          color="primary"
          className={classes.button}
        >
          Send report
        </Button>
      </div>
    </>
  );
};

ReportIssue.propTypes = {
  error: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ReportIssue;
